/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'transmute/get';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { CALYPSO_LIGHT, KOALA } from 'HubStyleTokens/colors';
import { WEB_FONT_MEDIUM_WEIGHT } from 'HubStyleTokens/misc';
import { getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getCallProvidersFromOmnibusResult } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIIcon from 'UIComponents/icon/UIIcon';
import PhoneFromNumberOptions from './PhoneFromNumberOptions';
import CallDeviceOptions from './CallDeviceOptions';
import * as CallStatus from 'calling-internal-common/widget-status/constants/CallWidgetStates';
import getCallingCapabilities from 'calling-global-api/methods/getCallingCapabilities';
import setCallingMethod from 'calling-global-api/methods/setCallingMethod';
import setCallingProviderSettings from 'calling-global-api/methods/setCallingProviderSettings';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { CALL_FROM_PHONE } from 'calling-internal-common/constants/CallMethods';
import { CANDY_APPLE } from 'HubStyleTokens/colors';
import { getIsHubSpotProvidedNumber } from 'calling-lifecycle-internal/operators/from-number/getIsHubSpotProvidedNumber';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CurrentSettingsWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfo__CurrentSettingsWrapper",
  componentId: "w7jyqb-0"
})(["border-style:solid;border-width:0 0 2px 0;border-color:", ";font-weight:", ";"], KOALA, WEB_FONT_MEDIUM_WEIGHT);
const CallSettingWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfo__CallSettingWrapper",
  componentId: "w7jyqb-1"
})(["cursor:pointer;background-color:transparent;&.is--disabled{opacity:0.5;}&:hover{background-color:", ";}"], CALYPSO_LIGHT);
const StyledUIIcon = styled(UIIcon).withConfig({
  displayName: "CurrentSettingsInfo__StyledUIIcon",
  componentId: "w7jyqb-2"
})(["float:right;"]);
const BodyWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfo__BodyWrapper",
  componentId: "w7jyqb-3"
})(["margin:-20px -24px 0;"]);
function CurrentSettingsInfo({
  appIdentifier,
  asyncSettingsOmnibus,
  selectedCallProvider,
  selectedFromNumber,
  selectedCallMethod,
  scopes,
  limits,
  widgetState,
  onSetCallStatus,
  onSetThirdPartyActive,
  disabled,
  enableBiDirectionalProviderSwitching
}) {
  const providerName = useMemo(() => get('name', selectedCallProvider), [selectedCallProvider]);
  const [showPhoneFromNumberOptions, setShowPhoneFromNumberOptions] = useState(false);
  const [showDeviceOptions, setShowDeviceOptions] = useState(false);
  const [hasCallingProvidersCapability, setHasCallingProvidersCapability] = useState(true);
  useEffect(() => {
    getCallingCapabilities().then(({
      callingProviders
    }) => {
      setHasCallingProvidersCapability(callingProviders);
    });
  }, []);
  const handleShowPhoneFromNumberOptions = useCallback(() => {
    setShowPhoneFromNumberOptions(true);
    setShowDeviceOptions(false);
  }, []);
  const handleShowDeviceOptionsOptions = useCallback(() => {
    setShowDeviceOptions(true);
    setShowPhoneFromNumberOptions(false);
  }, []);
  const resetWidget = useCallback(() => {
    const callStatus = widgetState.get('callStatus');
    const isThirdPartyActive = widgetState.get('isThirdPartyActive');
    if (callStatus === CallStatus.ENDED) {
      onSetCallStatus(CallStatus.READY);
    }
    if (isThirdPartyActive) {
      onSetThirdPartyActive(false);
    }
  }, [widgetState, onSetCallStatus, onSetThirdPartyActive]);
  const handleSelectFromNumber = useCallback(key => {
    resetWidget();
    const selectedProviderName = selectedCallProvider.get('name');
    const nextProviderName = key.providerName;
    const nextFromNumber = key.fromNumber;
    const omnibusData = getData(asyncSettingsOmnibus);
    const callingProviders = getCallProvidersFromOmnibusResult(omnibusData);
    const callingProvider = callingProviders.find(provider => get('name', provider) === nextProviderName);
    if (selectedProviderName !== nextProviderName) {
      CommunicatorLogger.log('calling_ProviderSwitch', {
        action: 'provider switch',
        activity: 'call',
        channel: 'outbound call',
        switchFrom: selectedProviderName,
        switchTo: nextProviderName,
        switchSource: 'numbers popover'
      });
    }
    if (selectedFromNumber !== nextFromNumber) {
      CommunicatorLogger.log('Calling_DropDownInteractions', {
        action: 'change "from" number',
        source: appIdentifier,
        selectFromNumberType: nextFromNumber.get('source'),
        previousfromNumberType: selectedFromNumber.get('source')
      });
    }
    setCallingProviderSettings({
      callingProvider,
      fromNumber: nextFromNumber
    });
  }, [appIdentifier, asyncSettingsOmnibus, resetWidget, selectedCallProvider, selectedFromNumber]);
  const handleSelectDevice = useCallback(method => {
    resetWidget();
    setCallingMethod(method);
    if (selectedCallMethod !== method) {
      CommunicatorLogger.log('Calling_DropDownInteractions', {
        action: 'change calling device',
        source: appIdentifier,
        previousDeviceType: selectedCallMethod,
        selectDeviceType: method
      });
    }
  }, [appIdentifier, resetWidget, selectedCallMethod]);
  const isHubSpotProvidedNumber = useMemo(() => getIsHubSpotProvidedNumber(selectedFromNumber), [selectedFromNumber]);
  const isCallingFromPhoneOnHubspotProvidedNumber = useMemo(() => isHubSpotProvidedNumber && selectedCallMethod === CALL_FROM_PHONE, [isHubSpotProvidedNumber, selectedCallMethod]);
  let numberOrProvider = providerName;
  if (selectedFromNumber) {
    if (selectedFromNumber.get('nickname') && selectedFromNumber.get('nickname').length) {
      numberOrProvider = `${selectedFromNumber.get('nickname')} ${selectedFromNumber.get('friendlyName')}`;
    } else if (selectedFromNumber.get('friendlyName')) {
      numberOrProvider = selectedFromNumber.get('friendlyName');
    }
  }
  return numberOrProvider ? /*#__PURE__*/_jsxs(CurrentSettingsWrapper, {
    children: [/*#__PURE__*/_jsx(UIPopover, {
      open: showPhoneFromNumberOptions,
      onOpenChange: () => setShowPhoneFromNumberOptions(false),
      animateOnToggle: true,
      width: "300px",
      content: {
        body: /*#__PURE__*/_jsx(UITooltip, {
          disabled: !disabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `callSettingsWidget.disableTooltip`
          }),
          children: /*#__PURE__*/_jsx(BodyWrapper, {
            children: /*#__PURE__*/_jsx(PhoneFromNumberOptions, {
              asyncSettingsOmnibus: asyncSettingsOmnibus,
              selectedProviderName: providerName,
              selectedFromNumber: selectedFromNumber,
              hasCallingProvidersCapability: hasCallingProvidersCapability,
              onSelect: handleSelectFromNumber,
              disabled: disabled,
              scopes: scopes,
              limits: limits,
              enableBiDirectionalProviderSwitching: enableBiDirectionalProviderSwitching
            })
          })
        })
      },
      placement: "right bottom",
      arrowSize: "none",
      children: /*#__PURE__*/_jsxs(CallSettingWrapper, {
        className: "p-x-4 p-y-3",
        onClick: handleShowPhoneFromNumberOptions,
        "data-selenium-test": "open-pre-call-popup-from-numbers",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "callSettingsWidget.callFrom",
          options: {
            numberOrProvider
          }
        }), /*#__PURE__*/_jsx(StyledUIIcon, {
          name: "right",
          size: "xxs"
        })]
      })
    }), selectedCallMethod && /*#__PURE__*/_jsx(UIPopover, {
      open: showDeviceOptions,
      shouldCloseOnClick: () => setShowDeviceOptions(false),
      animateOnToggle: true,
      content: {
        body: /*#__PURE__*/_jsx(BodyWrapper, {
          children: /*#__PURE__*/_jsx(CallDeviceOptions, {
            device: selectedCallMethod,
            onSelect: handleSelectDevice,
            disabled: disabled,
            isHubSpotProvidedNumber: isHubSpotProvidedNumber
          })
        })
      },
      placement: "right bottom",
      arrowSize: "none",
      children: /*#__PURE__*/_jsxs(CallSettingWrapper, {
        className: "p-x-4 p-y-3",
        selected: selectedCallMethod,
        onClick: handleShowDeviceOptionsOptions,
        "data-selenium-test": "open-pre-call-popup-devices",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: `callSettingsWidget.device.${selectedCallMethod}`
        }), isCallingFromPhoneOnHubspotProvidedNumber && /*#__PURE__*/_jsx(UITooltip, {
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "callee-phone-numbers.switchToCallFromBrowser"
          }),
          children: /*#__PURE__*/_jsx(UIIcon, {
            "data-selenium-test": "call-from-phone-with-hubspot-number-warning",
            name: "warning",
            color: CANDY_APPLE,
            className: "m-left-2",
            size: 16
          })
        }), /*#__PURE__*/_jsx(StyledUIIcon, {
          name: "right",
          size: "xxs"
        })]
      })
    })]
  }) : null;
}
CurrentSettingsInfo.propTypes = {
  appIdentifier: PropTypes.string,
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  selectedCallProvider: RecordPropType('CallingProvider'),
  selectedFromNumber: RecordPropType('FromNumber'),
  selectedCallMethod: PropTypes.string,
  scopes: ImmutablePropTypes.map.isRequired,
  limits: ImmutablePropTypes.map.isRequired,
  widgetState: ImmutablePropTypes.map.isRequired,
  onSetCallStatus: PropTypes.func.isRequired,
  onSetThirdPartyActive: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  enableBiDirectionalProviderSwitching: PropTypes.bool.isRequired
};
export default /*#__PURE__*/memo(CurrentSettingsInfo);